import { useState, useEffect } from 'react';
import { Key, Eye, EyeOff, Save, Plus, Loader2, AlertCircle, Check, Pencil, X, Trash2 } from 'lucide-react';
import api from '../../services/api';

const AVAILABLE_SERVICES = [
  { code: 'review',   name: 'Review',        emoji: '\u2b50' },
  { code: 'page',     name: 'Page',          emoji: '\ud83d\udcc4' },
  { code: 'menu',     name: 'Menu Digitale', emoji: '\ud83c\udf7d\ufe0f' },
  { code: 'chat_ai',  name: 'Chat AI',       emoji: '\ud83d\udcac' },
  { code: 'agent_ai', name: 'Agent AI',      emoji: '\ud83e\udd16' },
  { code: 'connect',  name: 'Connect',       emoji: '\ud83d\udd17' },
  { code: 'display',  name: 'Display',       emoji: '\ud83d\udda5\ufe0f' },
];

const DEFAULT_SETTINGS = [
  { key: 'anthropic_api_key', description: 'API key Anthropic per generazione testi AI (Review)', is_secret: true, service: 'review' },
  { key: 'anthropic_api_key_chat', description: 'API key Anthropic default per Chat AI (usata se il bot non ha custom_api_key)', is_secret: true, service: 'chat_ai' },
];

function ServiceBadge({ serviceCode }) {
  const svc = AVAILABLE_SERVICES.find(s => s.code === serviceCode);
  if (!svc) return null;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 text-[11px] font-medium rounded-full border border-blue-200">
      <span>{svc.emoji}</span> {svc.name}
    </span>
  );
}

export default function PlatformSettingsManager() {
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(null);
  const [success, setSuccess] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [editServices, setEditServices] = useState({});
  const [editSecrets, setEditSecrets] = useState({});
  const [editDescriptions, setEditDescriptions] = useState({});
  const [visibleKeys, setVisibleKeys] = useState({});
  const [expandedKey, setExpandedKey] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newSetting, setNewSetting] = useState({ key: '', value: '', description: '', is_secret: false, service: '' });

  const fetchSettings = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.request('/admin/settings');
      setSettings(res.data || []);
    } catch (err) {
      if (err.message?.includes('platform_settings')) {
        setError('La tabella platform_settings non esiste ancora. Esegui la migrazione SQL dalla Supabase Dashboard.');
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSettings(); }, []);

  const handleSave = async (key) => {
    if (!hasChanges(key)) return;
    const value = editValues[key];

    setSaving(key);
    setError(null);
    try {
      const payload = {};
      // Invia il valore solo se è stato effettivamente modificato
      if (value !== undefined) payload.value = value;
      if (editServices[key] !== undefined) payload.service = editServices[key];
      if (editSecrets[key] !== undefined) payload.is_secret = editSecrets[key];

      await api.request(`/admin/settings/${encodeURIComponent(key)}`, {
        method: 'PUT',
        body: JSON.stringify(payload)
      });
      setSuccess(key);
      setEditValues(prev => { const n = { ...prev }; delete n[key]; return n; });
      setEditServices(prev => { const n = { ...prev }; delete n[key]; return n; });
      setEditSecrets(prev => { const n = { ...prev }; delete n[key]; return n; });
      setEditDescriptions(prev => { const n = { ...prev }; delete n[key]; return n; });
      setTimeout(() => setSuccess(null), 2000);
      await fetchSettings();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(null);
    }
  };

  const handleCreate = async () => {
    if (!newSetting.key.trim()) return;
    setSaving('new');
    setError(null);
    try {
      await api.request('/admin/settings', {
        method: 'POST',
        body: JSON.stringify(newSetting)
      });
      setNewSetting({ key: '', value: '', description: '', is_secret: false, service: '' });
      setShowAddForm(false);
      await fetchSettings();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(null);
    }
  };

  const initDefaults = async () => {
    setSaving('init');
    for (const def of DEFAULT_SETTINGS) {
      try {
        await api.request('/admin/settings', {
          method: 'POST',
          body: JSON.stringify(def)
        });
      } catch (e) { /* ignore duplicates */ }
    }
    setSaving(null);
    await fetchSettings();
  };

  const toggleVisibility = (key) => {
    setVisibleKeys(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleDelete = async (key) => {
    if (!window.confirm(`Eliminare l'impostazione "${key}"? Questa azione non è reversibile.`)) return;
    setSaving(key);
    setError(null);
    try {
      await api.request(`/admin/settings/${encodeURIComponent(key)}`, { method: 'DELETE' });
      setExpandedKey(null);
      await fetchSettings();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(null);
    }
  };

  const toggleExpand = (key) => {
    setExpandedKey(prev => prev === key ? null : key);
    // Reset edit states when collapsing
    if (expandedKey === key) {
      setEditServices(prev => { const n = { ...prev }; delete n[key]; return n; });
      setEditSecrets(prev => { const n = { ...prev }; delete n[key]; return n; });
      setEditDescriptions(prev => { const n = { ...prev }; delete n[key]; return n; });
    }
  };

  const hasChanges = (key) => {
    return editValues[key] !== undefined || editServices[key] !== undefined || editSecrets[key] !== undefined || editDescriptions[key] !== undefined;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Impostazioni Piattaforma</h2>
          <p className="text-sm text-gray-500 mt-1">API keys e configurazioni globali</p>
        </div>
        <div className="flex gap-2">
          {settings.length === 0 && (
            <button
              onClick={initDefaults}
              disabled={saving === 'init'}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
            >
              {saving === 'init' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Key className="w-4 h-4" />}
              Inizializza defaults
            </button>
          )}
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 text-sm font-medium"
          >
            <Plus className="w-4 h-4" />
            Aggiungi
          </button>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Add Form */}
      {showAddForm && (
        <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
          <h3 className="font-semibold text-gray-900">Nuova impostazione</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Chiave</label>
              <input
                type="text"
                value={newSetting.key}
                onChange={(e) => setNewSetting({ ...newSetting, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })}
                placeholder="es. openai_api_key"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Valore</label>
              <input
                type="text"
                value={newSetting.value}
                onChange={(e) => setNewSetting({ ...newSetting, value: e.target.value })}
                placeholder="sk-..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Descrizione</label>
              <input
                type="text"
                value={newSetting.description}
                onChange={(e) => setNewSetting({ ...newSetting, description: e.target.value })}
                placeholder="A cosa serve questa chiave"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Servizio associato</label>
              <select
                value={newSetting.service}
                onChange={(e) => setNewSetting({ ...newSetting, service: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
              >
                <option value="">Nessuno (globale)</option>
                {AVAILABLE_SERVICES.map(s => (
                  <option key={s.code} value={s.code}>{s.emoji} {s.name}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={newSetting.is_secret}
                onChange={(e) => setNewSetting({ ...newSetting, is_secret: e.target.checked })}
                className="rounded"
              />
              Valore segreto (mascherato nella visualizzazione)
            </label>
            <div className="flex gap-2">
              <button onClick={() => setShowAddForm(false)} className="px-3 py-1.5 text-gray-600 text-sm hover:bg-gray-100 rounded-lg">Annulla</button>
              <button
                onClick={handleCreate}
                disabled={saving === 'new' || !newSetting.key.trim()}
                className="px-4 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                {saving === 'new' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Salva'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Settings List */}
      <div className="space-y-3">
        {settings.map((setting) => {
          const isExpanded = expandedKey === setting.key;
          const currentService = editServices[setting.key] !== undefined ? editServices[setting.key] : (setting.service || '');

          return (
            <div key={setting.key} className="bg-white border border-gray-200 rounded-xl p-5">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Key className="w-4 h-4 text-gray-400" />
                    <code className="text-sm font-semibold text-gray-900">{setting.key}</code>
                    {setting.is_secret && (
                      <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-semibold rounded uppercase">Segreto</span>
                    )}
                    {setting.service && <ServiceBadge serviceCode={setting.service} />}
                  </div>
                  {setting.description && (
                    <p className="text-xs text-gray-500 mt-1 ml-6">{setting.description}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {success === setting.key && (
                    <span className="flex items-center gap-1 text-green-600 text-xs font-medium">
                      <Check className="w-3.5 h-3.5" /> Salvato
                    </span>
                  )}
                  <button
                    onClick={() => toggleExpand(setting.key)}
                    className={`p-1.5 rounded-lg transition-colors ${isExpanded ? 'bg-gray-100 text-gray-700' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'}`}
                    title="Modifica impostazione"
                  >
                    {isExpanded ? <X className="w-4 h-4" /> : <Pencil className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Value input */}
              <div className="flex items-center gap-2">
                <div className="flex-1 relative">
                  <input
                    type={setting.is_secret && !visibleKeys[setting.key] ? 'password' : 'text'}
                    value={editValues[setting.key] !== undefined ? editValues[setting.key] : (setting.is_secret ? '' : (setting.value || ''))}
                    onChange={(e) => setEditValues({ ...editValues, [setting.key]: e.target.value })}
                    placeholder={setting.is_secret ? (setting.value || 'Non configurato') : 'Non configurato'}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {setting.is_secret && (
                    <button
                      onClick={() => toggleVisibility(setting.key)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                    >
                      {visibleKeys[setting.key] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  )}
                </div>
                <button
                  onClick={() => handleSave(setting.key)}
                  disabled={saving === setting.key || !hasChanges(setting.key)}
                  className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {saving === setting.key ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Salva
                </button>
              </div>

              {/* Expanded edit panel */}
              {isExpanded && (
                <div className="mt-4 pt-4 border-t border-gray-100 space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Servizio associato</label>
                      <select
                        value={currentService}
                        onChange={(e) => setEditServices({ ...editServices, [setting.key]: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Nessuno (globale)</option>
                        {AVAILABLE_SERVICES.map(s => (
                          <option key={s.code} value={s.code}>{s.emoji} {s.name}</option>
                        ))}
                      </select>
                      <p className="text-[11px] text-gray-400 mt-1">Seleziona il servizio che utilizza questa API key</p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Visibilita</label>
                      <label className="flex items-center gap-2 mt-2 text-sm text-gray-700 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editSecrets[setting.key] !== undefined ? editSecrets[setting.key] : setting.is_secret}
                          onChange={(e) => setEditSecrets({ ...editSecrets, [setting.key]: e.target.checked })}
                          className="rounded"
                        />
                        Valore segreto (mascherato nella visualizzazione)
                      </label>
                    </div>
                  </div>
                  <div className="pt-3 border-t border-gray-100 flex justify-end">
                    <button
                      onClick={() => handleDelete(setting.key)}
                      disabled={saving === setting.key}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-red-600 hover:bg-red-50 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                    >
                      {saving === setting.key ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                      Elimina impostazione
                    </button>
                  </div>
                </div>
              )}

              {setting.updated_at && setting.value && (
                <p className="text-[11px] text-gray-400 mt-2 ml-1">
                  Ultimo aggiornamento: {new Date(setting.updated_at).toLocaleString('it-IT')}
                </p>
              )}
            </div>
          );
        })}

        {settings.length === 0 && !error && (
          <div className="text-center py-12 text-gray-500">
            <Key className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="font-medium">Nessuna impostazione configurata</p>
            <p className="text-sm mt-1">Clicca "Inizializza defaults" per creare le impostazioni base</p>
          </div>
        )}
      </div>
    </div>
  );
}
